import * as React from "react";
import { format as formatDate, getDay, startOfDay } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@bcc/lib/utils";
import { Button } from "@bcc/components/ui/button";
import { Calendar } from "@bcc/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@bcc/components/ui/popover";
import { setDateSelectTestId } from "./date-select.utils";

export type DayOfTheWeek =
  | "Sunday"
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sun"
  | "Mon"
  | "Tue"
  | "Wed"
  | "Thu"
  | "Fri"
  | "Sat";

const DAY_NAME_TO_INDEX: Record<string, number> = {
  sunday: 0,
  sun: 0,
  monday: 1,
  mon: 1,
  tuesday: 2,
  tue: 2,
  wednesday: 3,
  wed: 3,
  thursday: 4,
  thu: 4,
  friday: 5,
  fri: 5,
  saturday: 6,
  sat: 6,
};

export interface DateSelectProps {
  value?: string;
  onChange: (date: Date | undefined) => void;
  onSelect?: (date: Date) => void;
  selectedDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  exclude?: DayOfTheWeek[];
  excludedDates?: Date[];
  isDisabled?: boolean;
  isClearable?: boolean;
  name?: string;
  testId?: string;
  placeholder?: string;
  format?: string;
}

function DateSelect({
  value,
  onChange,
  onSelect,
  selectedDate,
  minDate,
  maxDate,
  exclude,
  excludedDates,
  isDisabled = false,
  isClearable = false,
  name,
  testId,
  placeholder,
  format = "PPP",
}: DateSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const resolvedTestId = testId ?? (name ? setDateSelectTestId(name) : setDateSelectTestId("default"));
  const resolvedPlaceholder = placeholder ?? t("components.dateSelect.placeholder");

  const currentDate = React.useMemo(() => {
    if (selectedDate) return selectedDate;
    if (value) return new Date(value);
    return undefined;
  }, [selectedDate, value]);

  const excludedDayIndices = React.useMemo(() => {
    if (!exclude || exclude.length === 0) return undefined;
    return exclude.map((day) => DAY_NAME_TO_INDEX[day.toLowerCase()]).filter((idx) => idx !== undefined);
  }, [exclude]);

  const excludedDateTimes = React.useMemo(() => {
    if (!excludedDates || excludedDates.length === 0) return undefined;
    return excludedDates.map((d) => d.getTime());
  }, [excludedDates]);

  const isDateDisabled = React.useCallback(
    (date: Date) => {
      if (excludedDayIndices && excludedDayIndices.includes(getDay(date))) {
        return true;
      }
      if (minDate && startOfDay(date) < startOfDay(minDate)) return true;
      if (maxDate && startOfDay(date) > startOfDay(maxDate)) return true;
      if (excludedDateTimes && excludedDateTimes.includes(date.getTime())) {
        return true;
      }
      return false;
    },
    [excludedDayIndices, minDate, maxDate, excludedDateTimes]
  );

  const hasMinDateError = React.useMemo(() => {
    if (!currentDate || !minDate) return false;
    return startOfDay(currentDate) < startOfDay(minDate);
  }, [currentDate, minDate]);

  const hasMaxDateError = React.useMemo(() => {
    if (!currentDate || !maxDate) return false;
    return startOfDay(currentDate) > startOfDay(maxDate);
  }, [currentDate, maxDate]);

  const handleSelect = React.useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      onChange(date);
      onSelect?.(date);
      setOpen(false);
    },
    [onChange, onSelect]
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setOpen(false);
      onChange(undefined);
    },
    [onChange]
  );

  return (
    <div data-testid={resolvedTestId} data-slot="date-select">
      <Popover open={open} onOpenChange={setOpen} modal>
        <div className="relative">
          <PopoverTrigger asChild testId={`${resolvedTestId}-trigger`}>
            <Button
              variant="outline"
              disabled={isDisabled}
              testId={`${resolvedTestId}-trigger`}
              className={cn(
                "w-full pl-3 pr-10 text-left font-normal",
                !currentDate && "text-muted-foreground",
                (hasMinDateError || hasMaxDateError) && "border-destructive"
              )}
            >
              {currentDate ? formatDate(currentDate, format) : resolvedPlaceholder}
            </Button>
          </PopoverTrigger>
          {isClearable && currentDate ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 size-8 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              aria-label={t("components.dateSelect.clear")}
              testId={`${resolvedTestId}-clear`}
            >
              <span aria-hidden="true">&times;</span>
            </Button>
          ) : (
            <CalendarIcon className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 opacity-50" />
          )}
        </div>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={currentDate}
            onSelect={handleSelect}
            disabled={isDateDisabled}
            defaultMonth={currentDate ?? minDate}
          />
        </PopoverContent>
      </Popover>
      {hasMinDateError && (
        <p className="text-sm text-destructive mt-1" data-testid={`${resolvedTestId}-min-date-error`}>
          {t("components.dateSelect.errors.minDate")}
        </p>
      )}
      {hasMaxDateError && (
        <p className="text-sm text-destructive mt-1" data-testid={`${resolvedTestId}-max-date-error`}>
          {t("components.dateSelect.errors.maxDate")}
        </p>
      )}
    </div>
  );
}

export { DateSelect };
